// proceduralLibrary/loops/loopDefs.js
//
// 20 animated wallpaper "loops" ported from the deck's AL[0..19]
// (notes5-gif-libraries.elevation.mjs:963-996, DL-063b). Each loop is data:
//   { id, label, motion, build(palette) -> { css, poster, keyframes } }
// where `css` is the animated inline style (picker preview only), `poster` is
// the NON-animated background used when the loop is applied to a live wallpaper
// surface (deck "wedge = static poster" law + reduced-motion), and the shared
// @keyframes live in AL_KEYFRAMES.
//
// ASPECT LAW (deck): motion lives on an oversized inner .al-layer inside a fixed
// .al-fx frame — see LoopPanel. Loops are recolorable via a palette triple; the
// motion timing is fixed (untouched by recolor).

const enc = (svg) =>
  svg
    .replace(/\n/g, "")
    .replace(/"/g, "'")
    .replace(/%/g, "%25")
    .replace(/#/g, "%23")
    .replace(/</g, "%3C")
    .replace(/>/g, "%3E")
    .replace(/&/g, "%26");

// tileable background helpers (fill = motif color, bg = tile color)
const bgDots = (fill, bg, r, s) =>
  enc(
    `<svg xmlns='http://www.w3.org/2000/svg' width='${s}' height='${s}'><rect width='${s}' height='${s}' fill='${bg}'/><circle cx='${s / 2}' cy='${s / 2}' r='${r}' fill='${fill}'/></svg>`
  );
const bgStripe = (fill, bg, s) =>
  enc(
    `<svg xmlns='http://www.w3.org/2000/svg' width='${s}' height='${s}'><rect width='${s}' height='${s}' fill='${bg}'/><rect width='${s / 2}' height='${s}' fill='${fill}'/></svg>`
  );
const bgDiag = (fill, bg, s) =>
  enc(
    `<svg xmlns='http://www.w3.org/2000/svg' width='${s}' height='${s}'><rect width='${s}' height='${s}' fill='${bg}'/><path d='M0 ${s} L${s} 0 M${-s / 2} ${s / 2} L${s / 2} ${-s / 2} M${s / 2} ${s * 1.5} L${s * 1.5} ${s / 2}' stroke='${fill}' stroke-width='${s / 2.4}'/></svg>`
  );
const bgChev = (fill, bg, s) =>
  enc(
    `<svg xmlns='http://www.w3.org/2000/svg' width='${s}' height='${s}'><rect width='${s}' height='${s}' fill='${bg}'/><path d='M0 ${s * 0.7} L${s / 2} ${s * 0.3} L${s} ${s * 0.7} M0 ${s * 1.2} L${s / 2} ${s * 0.8} L${s} ${s * 1.2} M0 ${s * 0.2} L${s / 2} ${-s * 0.2} L${s} ${s * 0.2}' stroke='${fill}' stroke-width='3' fill='none'/></svg>`
  );
const bgWave = (fill, bg, s) =>
  enc(
    `<svg xmlns='http://www.w3.org/2000/svg' width='${s}' height='${s / 2}'><rect width='${s}' height='${s / 2}' fill='${bg}'/><path d='M0 ${s / 4} Q${s / 4} 0 ${s / 2} ${s / 4} T${s} ${s / 4}' stroke='${fill}' stroke-width='3' fill='none'/></svg>`
  );
const bgPlus = (fill, bg, s) =>
  enc(
    `<svg xmlns='http://www.w3.org/2000/svg' width='${s}' height='${s}'><rect width='${s}' height='${s}' fill='${bg}'/><path d='M${s / 2} ${s / 2 - 5} v10 M${s / 2 - 5} ${s / 2} h10' stroke='${fill}' stroke-width='2.4'/></svg>`
  );
const ringSvg = (fill, bg) =>
  enc(
    `<svg xmlns='http://www.w3.org/2000/svg' width='150' height='150' viewBox='0 0 150 150'><rect width='150' height='150' fill='${bg}'/><g fill='none' stroke='${fill}' stroke-width='2.2'>` +
      Array.from({ length: 17 }, (_, k) => `<circle cx='57' cy='57' r='${6 + k * 8}'/>`).join("") +
      `</g></svg>`
  );

// [id, label, motion, builder(a=light,b=deep,c=accent) -> {bg, anim}]
// `bg` is the static background (poster); `anim` is the animation shorthand.
const DEFS = [
  ["spinRings", "Spin rings", "orbit 360°", (a, b, c) => ({ bg: `url("${ringSvg(b, c)}") center/150px 150px no-repeat ${c}`, anim: "alSpin 12s linear infinite", spin: true })],
  ["driftDots", "Drift dots", "pan →", (a, b, c) => ({ bg: `url("${bgDots(b, c, 3.5, 20)}")`, tp: "20px", anim: "alPanX 3.4s linear infinite" })],
  ["driftDiagonal", "Drift diagonal", "pan ↘", (a, b, c) => ({ bg: `url("${bgDots(b, c, 3.5, 20)}")`, tp: "20px", anim: "alPanXY 4.5s linear infinite" })],
  ["barberpole", "Barberpole", "pan ↓", (a, b, c) => ({ bg: `url("${bgDiag(b, c, 24)}")`, tp: "24px", anim: "alPanY 1.9s linear infinite" })],
  ["gridPan", "Grid pan", "pan ↘", (a, b, c) => ({ bg: `url("${bgDots(b, c, 2.5, 22)}")`, tp: "22px", anim: "alPanXY 5.2s linear infinite" })],
  ["wavesScroll", "Waves scroll", "scroll →", (a, b, c) => ({ bg: `url("${bgWave(b, c, 40)}") repeat`, tp: "40px", anim: "alPanX 3.2s linear infinite" })],
  ["chevronMarch", "Chevron march", "march ↓", (a, b, c) => ({ bg: `url("${bgChev(b, c, 24)}")`, tp: "24px", anim: "alPanY 2.6s linear infinite" })],
  ["plusDrift", "Plus drift", "pan →", (a, b, c) => ({ bg: `url("${bgPlus(b, c, 22)}")`, tp: "22px", anim: "alPanX 4s linear infinite" })],
  ["spinGradient", "Spin gradient", "turn 360°", (a, b, c) => ({ bg: `conic-gradient(from 0deg,${b},${a},${c},${b})`, anim: "alSpin 9s linear infinite", spin: true })],
  ["hueCycle", "Hue cycle", "hue 360°", (a, b, c) => ({ bg: `linear-gradient(120deg,${b},${a})`, anim: "alHue 6s linear infinite" })],
  ["auroraShift", "Aurora shift", "drift ⇄", (a, b, c) => ({ bg: `linear-gradient(120deg,${a},${c},${b},${a})`, size: "280% 280%", anim: "alAurora 6s ease-in-out infinite alternate" })],
  ["breatheBlob", "Breathe blob", "pulse", (a, b, c) => ({ bg: `radial-gradient(circle at 50% 50%,${b} 0 26%,${c} 60%)`, anim: "alBreathe 3.4s ease-in-out infinite", spin: true })],
  ["twinkleDots", "Twinkle dots", "fade", (a, b, c) => ({ bg: `url("${bgDots(b, c, 3.5, 18)}")`, anim: "alTwinkle 2.4s ease-in-out infinite" })],
  ["diagonalScroll", "Diagonal scroll", "scroll ↘", (a, b, c) => ({ bg: `url("${bgStripe(b, c, 24)}")`, tp: "24px", anim: "alPanX 2.2s linear infinite" })],
  ["verticalScroll", "Vertical scroll", "scroll ↓", (a, b, c) => ({ bg: `url("${bgWave(b, c, 40)}") repeat`, tp: "20px", anim: "alPanY 2.8s linear infinite" })],
  ["confettiDrift", "Confetti drift", "pan ↘", (a, b, c) => ({ bg: `url("${bgPlus(b, c, 20)}")`, tp: "20px", anim: "alPanXY 4.8s linear infinite" })],
  ["slowDrift", "Slow drift", "pan →", (a, b, c) => ({ bg: `url("${bgDots(b, c, 3, 16)}")`, tp: "16px", anim: "alPanX 6s linear infinite" })],
  ["waveScrollV", "Wave scroll", "scroll ↓", (a, b, c) => ({ bg: `url("${bgWave(b, c, 40)}") repeat`, tp: "20px", anim: "alPanY 3.4s linear infinite" })],
  ["dotPanSlow", "Dot pan", "pan →", (a, b, c) => ({ bg: `url("${bgDots(b, c, 2.5, 18)}")`, tp: "18px", anim: "alPanX 5s linear infinite" })],
  ["stripeScroll", "Stripe scroll", "scroll →", (a, b, c) => ({ bg: `url("${bgStripe(b, c, 22)}")`, tp: "22px", anim: "alPanX 2.8s linear infinite" })],
];

// Shared keyframes (injected once by LoopPanel and, when a loop is applied to a
// live surface with motion enabled, by the global loop stylesheet).
export const AL_KEYFRAMES = `
@keyframes alPanX{to{background-position:var(--tp,20px) 0}}
@keyframes alPanY{to{background-position:0 var(--tp,20px)}}
@keyframes alPanXY{to{background-position:var(--tp,20px) var(--tp,20px)}}
@keyframes alSpin{to{transform:rotate(360deg)}}
@keyframes alHue{to{filter:hue-rotate(360deg)}}
@keyframes alBreathe{0%,100%{transform:scale(1)}50%{transform:scale(1.16)}}
@keyframes alTwinkle{0%,100%{opacity:.4}50%{opacity:1}}
@keyframes alAurora{0%{background-position:0% 50%}100%{background-position:100% 50%}}
`;

const DEFAULT_PAL = ["#B39DFB", "#7C5CF6", "#EDE7FF"];

// Build the render descriptor for a loop id + palette [light, deep, accent].
// Returns null for unknown ids. `spin` marks loops whose motion is transform/
// scale based (cannot be a plain background-position pan) — those only animate
// on the oversized inner layer, never on a live wallpaper surface directly.
export const buildLoop = (id, palette) => {
  const entry = LOOP_BY_ID[id];
  if (!entry) return null;
  const [a, b, c] = palette && palette.length === 3 ? palette : DEFAULT_PAL;
  const d = entry.builder(a, b, c);
  const tpVar = d.tp ? `--tp:${d.tp};` : "";
  const sizeVar = d.size ? `background-size:${d.size};` : "";
  return {
    id,
    // animated inline style for the picker preview layer
    css: `background:${d.bg};${sizeVar}${tpVar}animation:${d.anim}`,
    // static poster (no animation) for the applied wallpaper surface
    poster: `${d.bg}`,
    spin: !!d.spin,
  };
};

export const LOOPS = DEFS.map(([id, label, motion]) => ({ id, label, motion }));
export const LOOP_BY_ID = DEFS.reduce((acc, [id, label, motion, builder]) => {
  acc[id] = { id, label, motion, builder };
  return acc;
}, {});
